import { ref, computed } from 'vue'
import { useQueryClient } from '@tanstack/vue-query'
import { workflowService } from '../../services/workflowService.js'
import { invalidateNavBadges } from '../ui/useNavBadges.js'
import { useCurrentUser } from '../auth/useCurrentUser.js'

/**
 * Composable for managing the reassessment modal
 * @param {Object} options
 * @param {Function} options.refetch - Function to refetch prospects
 */
export function useReassessModal({ refetch }) {
  const queryClient = useQueryClient()
  const { getApiUserInfo } = useCurrentUser()

  const showReassessModal = ref(false)
  const selectedProspectForReassess = ref(null)
  const isReassessing = ref(false)
  const reassessError = ref('')

  function openReassessModal(prospect, event) {
    event?.stopPropagation()
    selectedProspectForReassess.value = prospect
    reassessError.value = ''
    showReassessModal.value = true
  }

  function closeReassessModal() {
    showReassessModal.value = false
    selectedProspectForReassess.value = null
    reassessError.value = ''
  }

  async function handleReassessSubmit(reason) {
    isReassessing.value = true
    reassessError.value = ''

    try {
      await workflowService.reassess(selectedProspectForReassess.value.id, getApiUserInfo(), reason)

      closeReassessModal()
      invalidateNavBadges(queryClient, { beoordelen: true, concretiseren: true })
      refetch()
      return true
    } catch (error) {
      console.error('Error reassessing prospect:', error)
      reassessError.value = error.response?.data?.error || 'Fout bij opnieuw beoordelen'
      return false
    } finally {
      isReassessing.value = false
    }
  }

  const reassessModalProps = computed(() => ({
    open: showReassessModal.value,
    prospect: selectedProspectForReassess.value,
    isSubmitting: isReassessing.value,
    error: reassessError.value
  }))

  return {
    showReassessModal,
    selectedProspectForReassess,
    isReassessing,
    reassessError,
    openReassessModal,
    closeReassessModal,
    handleReassessSubmit,
    reassessModalProps
  }
}
